import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Flex, Icon
} from 'components/ui';
import {
    colors, space, fontSizes, shadows, radii, mediaQueries, borders
} from 'style/config';
import CompareModal from 'components/Catalog/ProductGrid/CompareModal';
import useCompareProducts, { useCompareState } from 'components/Catalog/ProductGrid/hooks/useCompareProducts';
import ProductCompareActions from 'actions/ProductCompareActions';
import ProductCard from 'components/Catalog/ProductGrid/CompareDrawer/ProductCard/ProductCard';
import CompareDrawerButtons from 'components/Catalog/ProductGrid/CompareDrawer/CompareDrawerButtons/CompareDrawerButtons';
import mediaUtils from 'utils/Media';

const { Media } = mediaUtils;

const DRAWER_ANIMATION_DURATION = 300;
const MINIMIZED_HEIGHT = 56;
const MINIMIZED_HEIGHT_DESKTOP = 72;
const MAXIMIZED_FOOTER_HEIGHT = 60;
const MAX_PRODUCTS = 3;

const EmptySlot = ({ css }) => {
    return (
        <Box css={css}>
            {/* Small screen SVG */}
            <Media lessThan='md'>
                <svg
                    width='74'
                    height='74'
                    viewBox='0 0 74 74'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{ display: 'block', width: '100%', height: '100%' }}
                >
                    <rect
                        x='1'
                        y='1'
                        width='71.75'
                        height='71.75'
                        rx='3'
                        fill='white'
                    />
                    <rect
                        x='1'
                        y='1'
                        width='71.75'
                        height='71.75'
                        rx='3'
                        stroke='#888888'
                        strokeWidth='2'
                        strokeDasharray='10 10'
                        fill='none'
                    />
                </svg>
            </Media>
            {/* Large screen SVG */}
            <Media greaterThanOrEqual='md'>
                <svg
                    width='100%'
                    height='106'
                    viewBox='0 0 406 106'
                    preserveAspectRatio='none'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{ display: 'block' }}
                >
                    <rect
                        x='1'
                        y='1'
                        width='403.333'
                        height='104'
                        rx='3'
                        fill='white'
                    />
                    <rect
                        x='1'
                        y='1'
                        width='403.333'
                        height='104'
                        rx='3'
                        stroke='#888888'
                        strokeWidth='2'
                        strokeDasharray='10 10'
                        fill='none'
                    />
                </svg>
            </Media>
        </Box>
    );
};

const CompareDrawer = ({ isOpen, getText, categoryName }) => {
    const dispatch = useDispatch();
    const selectedProducts = useCompareProducts();
    const { shouldReopenModal } = useCompareState();
    const [isMinimized, setIsMinimized] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const previousProductCountRef = useRef(0);
    const previousIsOpenRef = useRef(isOpen);
    const previousCategoryRef = useRef(categoryName);

    // Expand drawer when products are added
    useEffect(() => {
        const currentCount = selectedProducts.length;
        const prevCount = previousProductCountRef.current;
        const wasOpen = previousIsOpenRef.current;

        const productsAdded = currentCount > prevCount;
        const isOpening = isOpen && !wasOpen;

        if (productsAdded && (isOpen || isOpening)) {
            setIsMinimized(false);
        }

        previousProductCountRef.current = currentCount;
        previousIsOpenRef.current = isOpen;
    }, [selectedProducts.length, isOpen]);

    // Minimize when navigating between categories
    useEffect(() => {
        if (previousCategoryRef.current && categoryName && previousCategoryRef.current !== categoryName) {
            setIsMinimized(true);
        }

        previousCategoryRef.current = categoryName;
    }, [categoryName]);

    useEffect(() => {
        if (selectedProducts.length === 0) {
            setIsModalOpen(false);
        }
    }, [selectedProducts.length]);

    // Reopen the Compare Modal when the user navigates back from a PDP.
    // The flag is set in localstorage before PDP navigation and consumed
    // by loadCompareState on the return trip to the PLP/SRP.
    useEffect(() => {
        if (shouldReopenModal && selectedProducts.length >= 2) {
            setIsModalOpen(true);
            setIsMinimized(false);
            dispatch(ProductCompareActions.clearCompareModalReopenFlag());
        }
    }, [shouldReopenModal, selectedProducts.length, dispatch]);

    const handleToggleMinimize = () => {
        setIsMinimized(prevState => !prevState);
    };

    const handleClearAll = () => {
        dispatch(ProductCompareActions.clearProducts());
    };

    const handleCompare = () => {
        if (selectedProducts.length >= 2) {
            setIsModalOpen(true);
        }
    };

    const handleModalDismiss = () => {
        setIsModalOpen(false);
        setIsMinimized(true);
    };

    const handleProductRemove = productId => {
        dispatch(ProductCompareActions.removeProduct(productId));
    };

    const productCount = selectedProducts.length;

    if (!isOpen || productCount === 0) {
        return null;
    }

    const titleText = `${getText('compareProducts')} (${productCount} ${getText('of')} ${MAX_PRODUCTS})`;

    return (
        <Box
            css={styles.drawerContainer(isOpen)}
            data-at={Sephora.debug.dataAt('compare_drawer')}
        >
            <Box css={styles.drawerContent(isMinimized)}>
                {/* Header */}
                <Flex
                    alignItems='center'
                    justifyContent='space-between'
                    css={styles.header(isMinimized)}
                >
                    <Text
                        is='h4'
                        fontSize={['base', 'lg']}
                        fontWeight='bold'
                        lineHeight={['base', 'tight']}
                        data-at={Sephora.debug.dataAt('compare_drawer_title')}
                    >
                        {titleText}
                    </Text>
                    {/* Desktop (lg+): Clear all, Compare buttons, and minimize caret grouped together */}
                    <Media greaterThanOrEqual='md'>
                        <Flex
                            alignItems='center'
                            gap={space[4]}
                            css={styles.headerActions}
                        >
                            <CompareDrawerButtons
                                onClearAll={handleClearAll}
                                onCompare={handleCompare}
                                productCount={productCount}
                                getText={getText}
                            />
                            <button
                                type='button'
                                onClick={handleToggleMinimize}
                                aria-label={isMinimized ? getText('maximize') : getText('minimize')}
                                css={styles.toggleButton}
                                data-at={Sephora.debug.dataAt('compare_drawer_toggle')}
                            >
                                <Icon
                                    name={isMinimized ? 'caretUp' : 'caretDown'}
                                    size={24}
                                    aria-hidden={true}
                                />
                            </button>
                        </Flex>
                    </Media>
                    {/* Mobile/Tablet: Only minimize caret */}
                    <Media lessThan='md'>
                        <button
                            type='button'
                            onClick={handleToggleMinimize}
                            aria-label={isMinimized ? getText('maximize') : getText('minimize')}
                            css={styles.toggleButton}
                            data-at={Sephora.debug.dataAt('compare_drawer_toggle')}
                        >
                            <Icon
                                name={isMinimized ? 'caretUp' : 'caretDown'}
                                size={24}
                                aria-hidden={true}
                            />
                        </button>
                    </Media>
                </Flex>

                {/* Product Section */}
                {!isMinimized && (
                    <Box
                        css={styles.productSection}
                        data-at={Sephora.debug.dataAt('compare_drawer_products')}
                    >
                        <Flex css={styles.productSlotsContainer}>
                            {Array.from({ length: MAX_PRODUCTS }, (_, index) => {
                                const product = selectedProducts[index];
                                const isEmpty = !product;

                                return (
                                    <Box
                                        key={index}
                                        css={styles.productSlot}
                                        data-at={Sephora.debug.dataAt(`compare_drawer_slot_${index + 1}`)}
                                    >
                                        {isEmpty ? (
                                            <EmptySlot css={styles.emptySlot} />
                                        ) : (
                                            <ProductCard
                                                product={product}
                                                index={index}
                                                onRemove={handleProductRemove}
                                                getText={getText}
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                        </Flex>
                    </Box>
                )}

                {/* Footer with Clear all and Compare buttons - Mobile/Tablet only */}
                {!isMinimized && (
                    <Media lessThan='md'>
                        <CompareDrawerButtons
                            onClearAll={handleClearAll}
                            onCompare={handleCompare}
                            productCount={productCount}
                            getText={getText}
                        />
                    </Media>
                )}
            </Box>
            <CompareModal
                isOpen={isModalOpen}
                onDismiss={handleModalDismiss}
                selectedProducts={selectedProducts}
                handleProductRemove={handleProductRemove}
            />
        </Box>
    );
};

CompareDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    getText: PropTypes.func.isRequired,
    categoryName: PropTypes.string.isRequired
};

const styles = {
    drawerContainer: isOpen => ({
        position: 'fixed',
        left: 0,
        right: 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
        transition: `transform ${DRAWER_ANIMATION_DURATION}ms ease-in-out`,
        bottom: 'var(--bottomNavHeight, 64px)',
        [mediaQueries.md]: {
            bottom: 0
        }
    }),
    drawerContent: isMinimized => ({
        backgroundColor: colors.white,
        borderTop: `${borders[1]} ${colors.lightGray}`,
        boxShadow: shadows.medium,
        maxHeight: isMinimized ? `${MINIMIZED_HEIGHT}px` : 'calc(100vh - var(--bottomNavHeight, 64px))',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: `max-height ${DRAWER_ANIMATION_DURATION}ms ease-in-out`,
        [mediaQueries.md]: {
            height: isMinimized ? `${MINIMIZED_HEIGHT_DESKTOP}px` : '222px',
            maxHeight: isMinimized ? `${MINIMIZED_HEIGHT_DESKTOP}px` : '222px',
            minHeight: isMinimized ? '92px' : 'auto',
            padding: `${space[5]}px ${space[9]}px`
        }
    }),
    header: isMinimized => ({
        height: isMinimized ? `${MINIMIZED_HEIGHT}px` : 'auto',
        padding: `${space[4]}px`,
        paddingBottom: isMinimized ? `${space[4]}px` : 0,
        gap: space[4],
        justifyContent: 'space-between',
        [mediaQueries.md]: {
            padding: `0 0 ${space[5]}px 0`,
            minHeight: 'auto',
            height: 'auto'
        }
    }),
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: space[4]
    },
    toggleButton: {
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    productSection: {
        flex: 1,
        minHeight: '100px',
        overflowY: 'auto',
        overflowX: 'hidden',
        '-webkit-overflow-scrolling': 'touch',
        padding: `${space[4]}px`,
        position: 'relative',
        paddingBottom: `${MAXIMIZED_FOOTER_HEIGHT + space[4]}px`, // Add padding to account for fixed footer
        [mediaQueries.md]: {
            padding: 0,
            height: '106px',
            minHeight: '106px',
            overflow: 'visible'
        }
    },
    productPlaceholder: {
        minHeight: '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.gray,
        fontSize: fontSizes.sm
    },
    productSlotsContainer: {
        display: 'flex',
        gap: space[4],
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        position: 'relative',
        zIndex: 'calc(var(--layer-flyout) + 2)',
        flexWrap: 'wrap',
        [mediaQueries.md]: {
            gap: space[4],
            flexWrap: 'nowrap'
        }
    },
    productSlot: {
        flex: '0 0 auto',
        width: '74px',
        height: '74px',
        [mediaQueries.md]: {
            width: 'auto',
            flex: '1 0 0',
            height: '106px'
        }
    },
    emptySlot: {
        position: 'relative',
        borderRadius: `${radii[2]}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.white,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        [mediaQueries.md]: {
            minHeight: '106px',
            height: '106px'
        }
    }
};

export default wrapFunctionalComponent(CompareDrawer, 'CompareDrawer');
